import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Mic, Video, Wifi, Monitor, Smartphone, DoorOpen } from "lucide-react";

export default function IntercomSystem() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const features = [
    { icon: Mic, key: "audio", color: "#ffde21" },
    { icon: Video, key: "video", color: "#ffde21" },
    { icon: Wifi, key: "connectivity", color: "#ffde21" },
    { icon: Monitor, key: "multipleScreens", color: "#ffde21" },
    { icon: Smartphone, key: "mobileApp", color: "#ffde21" }
  ];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/intercomeimg.webp"
                  alt={t("intercomSystem")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  <DoorOpen size={16} />
                  {dir === "rtl" ? "تواصل ذكي" : "Smart Communication"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("intercomSystem")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {i18n.language === "ar"
                  ? "حلول الاتصال الداخلي الذكية التي تجمع بين الأمان والراحة في كل زاوية من منزلك."
                  : "Smart intercom solutions that combine security and convenience throughout your home."}
              </p>

              <Link to="/contact">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  style={{
                    background: '#ffde21',
                    color: '#2d2d2b'
                  }}
                >
                  {t("homepage.buttons.contactUs")}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مميزات النظام" : "System Features"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تقنيات متطورة للتواصل الآمن والسهل داخل المنزل"
                : "Advanced technologies for safe and easy communication within your home"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Title */}
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`intercom_features.${feature.key}`)}
                  </h3>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "لماذا نظام الانتركم؟" : "Why Intercom System?"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl text-center"
              style={{ background: 'rgba(255, 222, 33, 0.1)' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: '#ffde21' }}
              >
                <DoorOpen size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: 'white' }}
              >
                {dir === "rtl" ? "أمان محسّن" : "Enhanced Security"}
              </h3>
              <p style={{ color: 'white', opacity: 0.9 }}>
                {dir === "rtl"
                  ? "تحقق من هوية الزوار قبل فتح الباب"
                  : "Verify visitor identity before opening the door"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl text-center"
              style={{ background: 'rgba(255, 222, 33, 0.1)' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: '#ffde21' }}
              >
                <Smartphone size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: 'white' }}
              >
                {dir === "rtl" ? "تحكم عن بعد" : "Remote Control"}
              </h3>
              <p style={{ color: 'white', opacity: 0.9 }}>
                {dir === "rtl"
                  ? "افتح الباب وتحدث مع الزوار من أي مكان"
                  : "Open doors and talk to visitors from anywhere"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl text-center"
              style={{ background: 'rgba(255, 222, 33, 0.1)' }}
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: '#ffde21' }}
              >
                <Monitor size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: 'white' }}
              >
                {dir === "rtl" ? "تكامل شامل" : "Complete Integration"}
              </h3>
              <p style={{ color: 'white', opacity: 0.9 }}>
                {dir === "rtl"
                  ? "يتكامل مع نظام المنزل الذكي بالكامل"
                  : "Fully integrates with your smart home system"}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز للترقية؟" : "Ready to Upgrade?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على نظام انتركم متطور يجمع بين الأمان والراحة"
                : "Get an advanced intercom system that combines security and convenience"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.buttons.contactUs")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
